import { useCallback, useEffect, useRef } from "react";
import { DateTime, Duration } from "luxon";
import type { SmartCardRow } from "@/domains/database";
import { HEADS_UP_AUTO_REFRESH_ENABLED } from "@/components/heads-up/heads-up.constants";
import { useSmartViewSnapshots } from "./use-smart-view-snapshots";
import type { HeadsUpDataSourceResult } from "./use-data-source";

const AUTO_REFRESH_MAX_AGE_MS = Duration.fromObject({ hours: 1 }).as("milliseconds");
const AUTO_REFRESH_PENDING_KEY = "__auto_refresh_pending__";

interface UseSnapshotsArgs {
  smartViewId: string | null;
  viewRefreshedAt: string | null;
  cards: SmartCardRow[];
  cardsSignature: string;
  isSidebarHovered: boolean;
  isCardsLoading: boolean;
  isViewLoading: boolean;
  statuses: HeadsUpDataSourceResult["statuses"];
  refreshSmartView: HeadsUpDataSourceResult["actions"]["refreshSmartView"];
}

interface UseSnapshotsResult {
  displayedCards: SmartCardRow[];
  renderedRefreshedAt: string | null;
  hasPendingSnapshot: boolean;
  acceptPendingSnapshot: () => void;
}

export function useSnapshots({
  smartViewId,
  viewRefreshedAt,
  cards,
  cardsSignature,
  isSidebarHovered,
  isCardsLoading,
  isViewLoading,
  statuses,
  refreshSmartView,
}: UseSnapshotsArgs): UseSnapshotsResult {
  const lastAutoRefreshedRef = useRef<string | null>(null);
  const autoRefreshPendingRef = useRef(false);

  const {
    current: snapshotCurrent,
    pending: snapshotPending,
    pushSnapshot,
    acceptPending,
    resetSnapshots,
  } = useSmartViewSnapshots<{
    smartViewId: string;
    refreshedAt: string | null;
    cards: SmartCardRow[];
  }>();

  useEffect(() => {
    if (!HEADS_UP_AUTO_REFRESH_ENABLED) {
      return;
    }

    if (!smartViewId) {
      return;
    }

    if (
      statuses.isRefreshing ||
      statuses.isOperationInFlight ||
      statuses.isHydrating ||
      statuses.isRegenerating
    ) {
      return;
    }

    const refreshedAt = viewRefreshedAt ?? null;
    const hasRefreshedForCurrent =
      refreshedAt !== null
        ? lastAutoRefreshedRef.current === refreshedAt
        : lastAutoRefreshedRef.current === AUTO_REFRESH_PENDING_KEY;

    if (hasRefreshedForCurrent) {
      return;
    }

    const now = DateTime.now();
    const isStale = (() => {
      if (!refreshedAt) {
        return true;
      }

      const last = DateTime.fromISO(refreshedAt, { setZone: true });
      if (!last.isValid) {
        return true;
      }

      return now.diff(last).as("milliseconds") > AUTO_REFRESH_MAX_AGE_MS;
    })();

    if (!isStale) {
      return;
    }

    lastAutoRefreshedRef.current = refreshedAt ?? AUTO_REFRESH_PENDING_KEY;
    autoRefreshPendingRef.current = true;
    refreshSmartView({ isAuto: true });
  }, [
    smartViewId,
    viewRefreshedAt,
    statuses.isRefreshing,
    statuses.isOperationInFlight,
    statuses.isHydrating,
    statuses.isRegenerating,
    refreshSmartView,
  ]);

  useEffect(() => {
    if (!smartViewId) {
      resetSnapshots();
      autoRefreshPendingRef.current = false;
      lastAutoRefreshedRef.current = null;
      return;
    }

    const deferNextSnapshot =
      autoRefreshPendingRef.current && (isSidebarHovered || snapshotPending !== null);

    const isSnapshotDataPending = cards.length === 0 && (isCardsLoading || isViewLoading);
    if (isSnapshotDataPending) {
      autoRefreshPendingRef.current = false;
      return;
    }

    pushSnapshot(
      {
        versionKey: `${smartViewId}:${viewRefreshedAt ?? "NONE"}:${cardsSignature}`,
        value: {
          smartViewId,
          refreshedAt: viewRefreshedAt ?? null,
          cards,
        },
      },
      { defer: deferNextSnapshot }
    );

    autoRefreshPendingRef.current = false;
  }, [
    smartViewId,
    viewRefreshedAt,
    cards,
    cardsSignature,
    isSidebarHovered,
    isCardsLoading,
    isViewLoading,
    snapshotPending,
    pushSnapshot,
    resetSnapshots,
  ]);

  const renderedRefreshedAt = snapshotCurrent?.value.refreshedAt ?? viewRefreshedAt ?? null;
  const displayedCards = snapshotCurrent?.value.cards ?? cards;
  const hasPendingSnapshot = Boolean(snapshotPending);

  const acceptPendingSnapshot = useCallback(() => {
    acceptPending();
  }, [acceptPending]);

  return {
    displayedCards,
    renderedRefreshedAt,
    hasPendingSnapshot,
    acceptPendingSnapshot,
  };
}
